import type {
  GravarRascunhoDto,
  LiberacaoItemResponse,
  LoteRascunhoDto,
  PoliticaPrecoResponse,
  PreviaLoteResponse,
  PublicacaoResponse,
  PublicarDto,
  PublicarResponse,
  RascunhoResponse,
  EscopoItemDto,
  EscopoItemResponse,
} from '@triade/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, requisitar } from '@/lib/api';
import { useMe } from './me';
import { useConsulta, useMutacao } from './base';

/** Chaves invalidadas por qualquer mutacao de liberacao (lista, formacao e rascunho). */
const INVALIDAR = [['precificacao'], ['rascunho']];

/** GET /precificacao/politica: NEGOCIADO (o orcamento parte do sugerido) ou TABELA. */
export function usePoliticaPreco() {
  return useConsulta<PoliticaPrecoResponse>(['precificacao', 'politica'], '/precificacao/politica');
}

/** GET /precificacao/rascunho: so com `praticado.gravar` (sem a permissao a consulta nem roda). */
export function useRascunho() {
  const me = useMe();
  const podeGravar = me.data?.permissoes.includes('praticado.gravar') ?? false;
  return useConsulta<RascunhoResponse>(['rascunho'], '/precificacao/rascunho', { ativo: podeGravar });
}

/** PUT /precificacao/rascunho: grava as edicoes no rascunho (nao publica). */
export function useGravarRascunho() {
  return useMutacao<RascunhoResponse, GravarRascunhoDto>({
    metodo: 'PUT',
    caminho: () => '/precificacao/rascunho',
    corpo: (v) => v,
    invalidar: INVALIDAR,
  });
}

/** DELETE /precificacao/rascunho: descarta o rascunho inteiro, ou so de um item e/ou uma tabela. */
export function useDescartarRascunho() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { itemId?: string; tabelaId?: string } | void>({
    mutationFn: (v) =>
      requisitar<void>('/precificacao/rascunho', {
        method: 'DELETE',
        query: { itemId: v?.itemId, tabelaId: v?.tabelaId },
      }),
    onSuccess: async () => {
      for (const chave of INVALIDAR) await qc.invalidateQueries({ queryKey: chave });
    },
  });
}

/** POST /precificacao/rascunho/lote: acoes em lote (sempre no rascunho). `aplicar: false` = so a previa. */
export function useLoteRascunho() {
  return useMutacao<PreviaLoteResponse, LoteRascunhoDto>({
    metodo: 'POST',
    caminho: () => '/precificacao/rascunho/lote',
    corpo: (v) => v,
    invalidar: INVALIDAR,
  });
}

/** POST /precificacao/publicar: publica o rascunho como lista vigente a partir de `validFrom`. */
export function usePublicar() {
  return useMutacao<PublicarResponse, PublicarDto>({
    metodo: 'POST',
    caminho: () => '/precificacao/publicar',
    corpo: (v) => v,
    invalidar: INVALIDAR,
  });
}

/** GET /precificacao/publicacoes: historico de publicacoes (mais recentes primeiro). */
export function usePublicacoes(ativo = true) {
  return useConsulta<PublicacaoResponse[]>(['precificacao', 'publicacoes'], '/precificacao/publicacoes', { ativo });
}

/** GET /precificacao/liberacao/item/:id: um item, todas as tabelas e condicoes (publicado, rascunho, efetivo). */
export function useLiberacaoItem(itemId: string | null) {
  return useConsulta<LiberacaoItemResponse>(
    ['precificacao', 'liberacao', itemId],
    `/precificacao/liberacao/item/${itemId ?? ''}`,
    { ativo: itemId !== null },
  );
}

/** GET /precificacao/liberacao/escopo/:id: onde o item e liberado (liberadoEmTudo = sem marcacao). */
export function useEscopoItem(itemId: string | null) {
  return useConsulta<EscopoItemResponse>(['precificacao', 'escopo', itemId], `/precificacao/liberacao/escopo/${itemId ?? ''}`, { ativo: itemId !== null });
}

/** PUT /precificacao/liberacao/escopo/:id: substitui as combinacoes do item (lista vazia = liberado em tudo). Vale na hora. */
export function useSalvarEscopo(itemId: string) {
  return useMutacao<EscopoItemResponse, EscopoItemDto>({
    metodo: 'PUT',
    caminho: () => `/precificacao/liberacao/escopo/${itemId}`,
    corpo: (v) => v,
    invalidar: INVALIDAR,
  });
}
